import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { twJoin } from "tailwind-merge";

import type { ChatDetailDto, ChatReplyDto, ChatReplyRequest } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useBool } from "@/hooks/useBool";
import { useSignalRHub, useSignalRInvocation, useSignalRSubscription } from "@/hooks/useSignalR";
import { useSlug } from "@/hooks/useSlug";
import type { ChatReplyPayload } from "@/modules/chats/components/ChatWindow";
import { ChatWindow } from "@/modules/chats/components/ChatWindow";
import { chatMutations, useChatQueries } from "@/queries/chats";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { EditGroupChatModal } from "./EditGroupChatModal";

export const ChatDetailPage = (): React.ReactNode => {
  const [chatReplies, setChatReplies] = useState<ChatReplyDto[]>([]);
  const [isEditGroupModalOpened, editGroupModalHandler] = useBool(false);
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const { uploadFormImage } = useUploadImage();
  const { uploadFormVideo } = useUploadVideo({});
  const { id: chatId } = useParams<{
    id: string;
  }>();
  const { signalRConnection } = useSignalRHub("chat-hub", {
    query: `userId=${sessionUser.id}&chatId=${chatId}`,
  });
  const { invoke: onReadMessage } = useSignalRInvocation(signalRConnection, "ChatMessageRead");
  const onNewChatMessage = useCallback(
    async (
      ...args: [
        {
          chatId: string;
          isGroupChat: boolean;
          latestReply: ChatReplyDto;
        },
      ]
    ) => {
      const latestChatReply = args[0].latestReply;
      setChatReplies((prev) => [...prev, latestChatReply]);
      await onReadMessage({
        chatId: chatId,
        userId: sessionUser.id,
      });
    },
    [setChatReplies, onReadMessage, chatId, sessionUser.id],
  );
  useSignalRSubscription(signalRConnection, "NewChatMessage", onNewChatMessage);
  const chatQueries = useChatQueries();
  const {
    data: chatDetails,
    isPending: isLoadingChatDetails,
    error: errorLoadingChatDetails,
  } = useQuery({
    ...chatQueries.details({
      chatId: chatId!,
    }),
    enabled: !!chatId,
  });
  const {
    data: repliesData,
    hasNextPage: hasMoreReplies,
    fetchNextPage: fetchMoreReplies,
    isPending: isLoadingReplies,
    isFetchingNextPage: isFetchingMoreReplies,
    error: errorLoadingReplies,
  } = useInfiniteQuery({
    ...chatQueries.listChatReplies({
      chatId: chatId!,
    }),
    enabled: !!chatId,
  });
  const addChatReplyMutation = chatMutations.useAddChatReply();

  useEffect(() => {
    setChatReplies(repliesData?.pages.flatMap((x) => x.items) ?? []);
  }, [repliesData]);

  const handleAddChatReply = async ({ message, images, videos }: ChatReplyPayload) => {
    if (!chatId) {
      return;
    }
    const payload: ChatReplyRequest = {
      text: message,
    };
    if (images.length > 0) {
      const uploadedImage = await uploadFormImage(images[0]);
      if (uploadedImage) {
        payload.imageId = uploadedImage.id;
      }
    }
    const uploadedVideoIds: string[] = [];
    if (videos.length > 0) {
      for (const video of videos) {
        const uploadedVideo = await uploadFormVideo(video);
        if (uploadedVideo) {
          uploadedVideoIds.push(uploadedVideo.id);
        }
      }
      payload.videoIds = uploadedVideoIds;
    }
    await addChatReplyMutation.mutateAsync({
      chatId,
      data: payload,
    });
  };

  const error = !chatId || errorLoadingChatDetails || errorLoadingReplies;
  if (isLoadingChatDetails || isLoadingReplies) {
    return <FullSizeLoader />;
  }
  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <DocumentPaper
      title={
        chatDetails.chatType === "single"
          ? chatDetails.privateChatInfo!.receiver.fullName
          : chatDetails.groupChatInfo!.name
      }
      renderTitle={(title) => {
        return (
          <ChatDetailPageTitle
            onClickEditChat={editGroupModalHandler.setTrue}
            {...{
              title,
              chatDetails,
            }}
          />
        );
      }}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("page.chat-detail.breacrumbs.chats"),
              to: routes.chats.list({
                slug,
              }),
            },
            {
              name: t("page.chat-detail.breacrumbs.chat-detail"),
            },
          ]}
        />
      }
    >
      <ChatWindow
        canChat={
          chatDetails?.chatType === "single"
            ? chatDetails.privateChatInfo!.receiver.chatEnabled
            : chatDetails.groupChatInfo!.isMember
        }
        chatReplies={chatReplies}
        hasMoreReplies={hasMoreReplies}
        fetchMoreReplies={fetchMoreReplies}
        isLoadingMoreReplies={isFetchingMoreReplies}
        sendReply={handleAddChatReply}
        chatName={chatDetails?.groupChatInfo?.name}
      />

      <EditGroupChatModal
        isOpened={isEditGroupModalOpened}
        onOpenChange={editGroupModalHandler.set}
        chatId={chatId}
        groupInfo={chatDetails.groupChatInfo!}
      />
    </DocumentPaper>
  );
};
interface ChatDetailPageTitleProps {
  title: string | undefined;
  chatDetails: ChatDetailDto;
  onClickEditChat: () => void;
}
const ChatDetailPageTitle = ({ title, chatDetails, onClickEditChat }: ChatDetailPageTitleProps) => {
  const isEditChatAvailable = chatDetails?.chatType === "group" && chatDetails.groupChatInfo?.isMember;

  return (
    <div className="flex w-full items-center gap-2 overflow-hidden md:gap-4">
      {chatDetails.chatType === "single" && <UserAvatar img={chatDetails.privateChatInfo!.receiver.avatar} />}
      {chatDetails.chatType === "group" && (
        <div className="flex size-10 items-center justify-center rounded-full border border-grey-100 text-grey-500">
          {chatDetails.groupChatInfo?.image ? (
            <UserAvatar img={chatDetails.groupChatInfo.image} />
          ) : (
            <Users01Icon size={24} />
          )}
        </div>
      )}
      <h1
        data-testid="chat-title"
        className={twJoin(
          "flex-1 truncate",
          isEditChatAvailable && "hover:cursor-pointer hover:text-aop-basic-blue-500",
        )}
        onClick={isEditChatAvailable ? onClickEditChat : undefined}
        role={isEditChatAvailable ? "button" : "heading"}
        tabIndex={isEditChatAvailable ? 0 : -1}
      >
        {title}
      </h1>
    </div>
  );
};
